import type {
  AggregateChild,
  AvailabilityCategory,
  AvailabilityResponse,
  CategoryItem,
} from "./types";
import { isSameDayClosed, todayCentral } from "./sameDayCutoff";

/**
 * Same-day cabana fallback (Son's Island).
 *
 * Upstream disagreement, reproduced 2026-08-16:
 *   - `/api/availability-calendar` reports today `available` at $199.
 *   - `/api/availability` returns NO `island_cabana` / `key_cabana` categories
 *     for today (later dates return both normally).
 *   - `/api/aggregate-children?package_id=271` for the SAME day correctly
 *     reports the 2 cabanas that Checkfront/the Hub show as bookable.
 *
 * So the inventory exists and only the category rollup drops it. When the
 * availability response has no cabana categories, we rebuild them from the
 * package children list, which is accurate. The durable fix belongs upstream;
 * this stays as a harmless safety net once that lands (it only runs when the
 * cabana categories are missing).
 */
export const DAY_CABANA_PACKAGE_ID = "271";

export const CABANA_CATEGORY_TYPES = ["island_cabana", "key_cabana"] as const;
export type CabanaCategoryType = (typeof CABANA_CATEGORY_TYPES)[number];

const DEFAULT_META: Record<CabanaCategoryType, { label: string; emoji: string; description: string }> = {
  island_cabana: {
    label: "Island Cabanas",
    emoji: "🏝️",
    description: "On land with private floating dock",
  },
  key_cabana: {
    label: "Key Cabanas",
    emoji: "🏝️",
    description: "Over-water cabana on its own key",
  },
};

/**
 * Island vs Key from the Checkfront unit name. Mirrors the code parsing used
 * by the property map (`A-1`, `B-4`, `K-3`), so cabanas added in the Hub are
 * classified automatically instead of via a hardcoded id list.
 */
export function classifyCabanaType(name: string): CabanaCategoryType | null {
  const m = name.match(/[#\s-]([ABCK])-?\s*(\d+)/i);
  if (!m) return /\bkey\b/i.test(name) ? "key_cabana" : null;
  return m[1].toUpperCase() === "K" ? "key_cabana" : "island_cabana";
}

/** Build synthetic cabana categories from the day-cabana package children. */
export function buildCabanaCategoriesFromChildren(
  children: AggregateChild[],
  templates: AvailabilityCategory[] = [],
  nightCount = 1,
): AvailabilityCategory[] {
  const byType = new Map<CabanaCategoryType, CategoryItem[]>();
  for (const child of children) {
    if (!child.available) continue;
    const type = classifyCabanaType(child.name);
    if (!type) continue;
    const list = byType.get(type) ?? [];
    list.push({
      id: String(child.id),
      name: child.name,
      price: typeof child.price === "number" ? child.price : 0,
      live_available_count: 1,
    });
    byType.set(type, list);
  }

  const out: AvailabilityCategory[] = [];
  for (const type of CABANA_CATEGORY_TYPES) {
    const items = byType.get(type);
    if (!items || items.length === 0) continue;
    const template = templates.find((t) => t.type === type);
    const prices = items.map((i) => i.price).filter((p) => p > 0);
    const lowest = prices.length ? Math.min(...prices) : 0;
    const highest = prices.length ? Math.max(...prices) : 0;
    out.push({
      type,
      label: template?.label ?? DEFAULT_META[type].label,
      emoji: template?.emoji ?? DEFAULT_META[type].emoji,
      description: template?.description ?? DEFAULT_META[type].description,
      overnight: false,
      perPerson: false,
      availableCount: items.length,
      total_available_units: items.length,
      nightCount,
      pricing: {
        lowest,
        highest,
        perNight: null as unknown as number,
        displayPrice: lowest ? `$${lowest}` : "",
      },
      items,
      // Add-on packages (extra car / extra person) come from a normal day's
      // response so the checkout steppers behave identically.
      packages: template?.packages ?? [],
    });
  }
  return out;
}

export interface CabanaFallbackDeps {
  fetchChildren: (packageId: string, checkin: string, checkout: string) => Promise<{ children: AggregateChild[] }>;
  /** Raw availability fetch (no fallback) used only to source category templates. */
  fetchTemplate: (checkin: string, checkout: string) => Promise<AvailabilityResponse>;
}

function priceOf(items: CategoryItem[]): { lowest: number; highest: number } {
  const prices = items.map((i) => i.price).filter((p) => p > 0);
  return {
    lowest: prices.length ? Math.min(...prices) : 0,
    highest: prices.length ? Math.max(...prices) : 0,
  };
}

/**
 * Merge cabana units that are bookable per the package children list but
 * missing from the availability category rollup.
 *
 * Verified upstream defect (2026-08-16): unit ids 12 (`Sugarloaf #B-2`) and
 * 14 (`Splish Splash #B-4`) are dropped from `/api/availability`'s cabana
 * categories on EVERY date while `/api/aggregate-children` reports them
 * bookable — e.g. Aug 17 rollup 26 island vs 28 real, Aug 22 rollup had no
 * island category at all even though B-2 was open. The merge only ever ADDS
 * genuinely available units, so it is a no-op once upstream is corrected.
 */
export function mergeMissingCabanaUnits(
  categories: AvailabilityCategory[],
  children: AggregateChild[],
  nightCount = 1,
): AvailabilityCategory[] {
  const known = new Set(
    categories
      .filter((c) => (CABANA_CATEGORY_TYPES as readonly string[]).includes(c.type))
      .flatMap((c) => (c.items ?? []).map((i) => String(i.id))),
  );
  const missing = children.filter(
    (child) => child.available && !known.has(String(child.id)) && classifyCabanaType(child.name),
  );
  if (missing.length === 0) return categories;

  const rebuilt = buildCabanaCategoriesFromChildren(missing, categories, nightCount);
  const out = categories.map((cat) => {
    const extra = rebuilt.find((r) => r.type === cat.type);
    if (!extra) return cat;
    const items = [...(cat.items ?? []), ...extra.items];
    const { lowest, highest } = priceOf(items);
    return {
      ...cat,
      items,
      availableCount: items.length,
      total_available_units: items.length,
      pricing: {
        ...cat.pricing,
        lowest,
        highest,
        displayPrice: lowest ? `$${lowest}` : cat.pricing?.displayPrice ?? "",
      },
    };
  });

  // Types the rollup dropped entirely get their own category, ahead of the
  // water-rental categories so cabanas stay the first thing guests see. Their
  // add-on packages (extra car / extra person) are borrowed from the sibling
  // cabana category so the checkout steppers behave identically.
  const sibling = categories.find(
    (c) => (CABANA_CATEGORY_TYPES as readonly string[]).includes(c.type) && (c.packages ?? []).length > 0,
  );
  const created = rebuilt
    .filter((r) => !categories.some((c) => c.type === r.type))
    .map((r) => (r.packages?.length ? r : { ...r, packages: sibling?.packages ?? [] }));
  return created.length ? [...created, ...out] : out;
}

function nextDayISO(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
}

/**
 * Reconcile an availability response against the real per-cabana inventory.
 * Runs on every date; returns `data` untouched when nothing is missing, when
 * the property is closed, or when the reconciliation itself fails.
 */
export async function applyCabanaFallback(
  data: AvailabilityResponse,
  checkin: string,
  checkout: string,
  deps: CabanaFallbackDeps,
  now: Date = new Date(),
): Promise<AvailabilityResponse> {
  if (!data?.success || data.open === false) return data;
  // Past dates need no reconciliation, and after the 6pm CT cutoff today is
  // intentionally closed — don't resurrect it.
  if (checkin < todayCentral(now) || isSameDayClosed(checkin, now)) return data;

  try {
    const existing = data.availability ?? [];
    const hasCabanaCategory = existing.some((c) =>
      (CABANA_CATEGORY_TYPES as readonly string[]).includes(c.type),
    );
    const [childrenResp, template] = await Promise.all([
      deps.fetchChildren(DAY_CABANA_PACKAGE_ID, checkin, checkout),
      // Only needed when a whole cabana category is absent — we then borrow
      // its label/description/add-on packages from a neighbouring date.
      hasCabanaCategory
        ? Promise.resolve(null)
        : deps.fetchTemplate(nextDayISO(checkin), nextDayISO(checkout)).catch(() => null),
    ]);
    const merged = mergeMissingCabanaUnits(
      existing,
      childrenResp?.children ?? [],
      data.nights || 1,
    );
    if (merged === existing) return data;
    // Fill label/packages for categories the rollup dropped entirely.
    const enriched = merged.map((cat) => {
      if (existing.includes(cat)) return cat;
      const t = template?.availability?.find((x) => x.type === cat.type);
      return t
        ? { ...cat, label: t.label, emoji: t.emoji, description: t.description, packages: t.packages ?? [] }
        : cat;
    });
    return { ...data, availability: enriched };
  } catch {
    // Never break the step because the safety net failed.
    return data;
  }
}
